import { ReactNode, useCallback, useState } from 'react';

import { Box, SpacingValues } from '../../Box';
import { Typography } from '../../Typography';
import { IconButton } from '../../IconButton';
import { List, ListItem } from '../../List';
import { Icon, IconComponentProps } from '../../Icon';

/**
 * Свойства компонента {@link DescriptionsItem}
 */
export interface DescriptionsItemProps {
  /**
   * Метка элемента.
   */
  label?: string;

  /**
   * Значение элемента.
   */
  value: string[] | string | ReactNode;

  /**
   * Отображать значение как список элементов.
   *
   * @defaultValue `false`
   */
  listItems?: boolean;

  /**
   * Дочерние элементы компонента.
   */
  children?: ReactNode;

  /**
   * Отступ между элементами.
   */
  gapBetweenItems?: SpacingValues;

  /**
   * Направление flex-контейнера.
   *
   * @defaultValue `'column'`
   */
  flexDirection?: 'column' | 'row';

  /**
   * Выравнивание элементов.
   *
   * @defaultValue `'flex-start'`
   */
  alignItems?: 'flex-start' | 'flex-end' | 'center' | 'stretch';

  /**
   * Стили для текста.
   *
   * @defaultValue `'body2'`
   */
  valueTextVariant?:
    | 'h1'
    | 'h2'
    | 'h3'
    | 'h4'
    | 'h5'
    | 'h6'
    | 'subtitle1'
    | 'subtitle2'
    | 'body1'
    | 'body2'
    | 'body3'
    | 'caption'
    | 'button'
    | 'overline'
    | 'inherit';

  /**
   * Цвет для иконки.
   */
  iconColor?: IconComponentProps['color'];

  /**
   * Максимальная длина списка.
   *
   * @defaultValue `4`
   */
  maxLength?: number;

  /**
   * Занимать всю ширину контейнера.
   *
   * @defaultValue `false`
   */
  fullWidth?: boolean;
}

/**
 * Элемент описания.
 *
 * @internal
 * @param label - Метка элемента.
 * @param value - Значение элемента.
 * @param listItems - Отображать значение как список элементов.
 * @param children - Дочерние элементы компонента.
 * @param gapBetweenItems - Отступ между элементами.
 * @param flexDirection - Направление flex-контейнера.
 * @param alignItems - Выравнивание элементов.
 * @param valueTextVariant - Стили для текста.
 * @param iconColor - Цвет для иконки.
 * @param maxLength - Максимальная длина списка.
 * @param fullWidth - Занимать всю ширину контейнера.
 * @returns  Элемент описания.
 *
 * @remarks
 *
 * Ссылка на свойства компонента {@link DescriptionsItemProps}
 */
export const DescriptionsItem = ({
  label,
  value,
  listItems = false,
  children,
  flexDirection = 'column',
  alignItems = 'flex-start',
  valueTextVariant = 'body2',
  iconColor,
  maxLength = 4,
  fullWidth = false,
}: DescriptionsItemProps) => {
  const [expanded, setExpanded] = useState(false);

  const handleExpandClick = useCallback(() => {
    setExpanded((prev) => !prev);
  }, []);

  return (
    <Box
      display="flex"
      flexDirection={flexDirection}
      gap="8"
      alignItems={alignItems}
    >
      {label && (
        <Box>
          <Typography variant="body3" color="text.light.tertirary">
            {`${label}:`}
          </Typography>
        </Box>
      )}
      {children ? (
        <Box
          width={fullWidth ? '100%' : 'auto'}
          display="flex"
          gap="8"
          flexWrap="wrap"
          alignItems="center"
        >
          <Typography variant={valueTextVariant} color="text.light.tertirary">
            {`${value}:`}
          </Typography>

          {children}
        </Box>
      ) : (
        <Box width={fullWidth ? '100%' : 'auto'}>
          <Box>
            {Array.isArray(value) && !listItems && (
              <Box display="flex" flexDirection="column" gap="16">
                {(expanded ? value : value.slice(0, maxLength)).map(
                  (itemValue: string) => (
                    <Box
                      key={itemValue}
                      display="flex"
                      flexDirection="column"
                      gap="4"
                    >
                      <Typography variant={valueTextVariant}>
                        {itemValue}
                      </Typography>
                    </Box>
                  ),
                )}
              </Box>
            )}

            {Array.isArray(value) && listItems && (
              <List listStyleType="disc" pl={1.6}>
                {(expanded ? value : value.slice(0, maxLength)).map(
                  (itemValue, index) => (
                    <ListItem key={itemValue}>
                      <Box pt={index === 0 ? 'unset' : '8'}>
                        <Typography variant={valueTextVariant}>
                          {itemValue}
                        </Typography>
                      </Box>
                    </ListItem>
                  ),
                )}
              </List>
            )}
          </Box>

          {!Array.isArray(value) && (
            <Box>
              <Typography variant={valueTextVariant}>{value}</Typography>
            </Box>
          )}

          <Box>
            {Array.isArray(value) && value.length > maxLength && (
              <Box>
                <Box
                  display="flex"
                  alignItems="center"
                  justifyContent="center"
                  mt="8"
                >
                  <IconButton
                    type="button"
                    size="small"
                    onClick={handleExpandClick}
                  >
                    <Icon
                      name={
                        expanded ? 'arrow_triangle_up' : 'arrow_triangle_down'
                      }
                      size="16"
                      color={iconColor}
                    />
                  </IconButton>
                </Box>
              </Box>
            )}
          </Box>
        </Box>
      )}
    </Box>
  );
};
